import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { toast } from "sonner";
import { Settings2, X, RefreshCw, Clock4, Info, AlertTriangle, Check, Ban, Timer } from "lucide-react";
import EmpPicker from "@/components/EmpPicker";
import { MAROON, inpL, ST_CHIP } from "@/components/kiyana/LeaveForm";

const card = "bg-white rounded-2xl border border-slate-100";
const TABS = ["menunggu", "disetujui", "ditolak"];
const TAB_LABEL = { menunggu: "Menunggu", disetujui: "Disetujui", ditolak: "Ditolak" };

function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtDay(ds) {
  try {
    return new Date(`${ds}T00:00:00`).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
  } catch { return ds; }
}

function SettingsModal({ settings, roles, onClose, onSaved }) {
  const [form, setForm] = useState({ default_rate: 15000, min_minutes: 30, role_rates: {} });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (settings) setForm({ default_rate: settings.default_rate, min_minutes: settings.min_minutes, role_rates: settings.role_rates || {} }); }, [settings]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/overtime/settings", { ...form, default_rate: parseInt(form.default_rate) || 0, min_minutes: parseInt(form.min_minutes) || 0 });
      toast.success("Pengaturan lembur tersimpan"); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ot-settings-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-2xl p-5 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-bold text-slate-900">Pengaturan Lembur</h3>
          <button data-testid="ot-settings-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Tarif default per jam</label>
            <input data-testid="ot-settings-rate" type="number" min="0" value={form.default_rate} onChange={(e) => setForm({ ...form, default_rate: e.target.value })} className={inpL} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Minimal lembur (menit)</label>
            <input data-testid="ot-settings-min" type="number" min="0" max="240" value={form.min_minutes} onChange={(e) => setForm({ ...form, min_minutes: e.target.value })} className={inpL} />
          </div>
        </div>
        <div>
          <div className="text-[11px] font-semibold text-slate-500 mb-1.5">Tarif per role/divisi <span className="font-normal text-slate-400">(kosongkan = ikuti default)</span></div>
          <div className="space-y-2">
            {roles.map((r) => (
              <div key={r.id} className="flex items-center gap-2">
                <span className="text-xs text-slate-600 w-28 truncate">{r.label}</span>
                <input data-testid={`ot-rate-${r.id}`} type="number" min="0" placeholder={String(form.default_rate)} value={form.role_rates?.[r.id] ?? ""}
                  onChange={(e) => {
                    const rr = { ...(form.role_rates || {}) };
                    if (e.target.value === "") delete rr[r.id]; else rr[r.id] = parseInt(e.target.value) || 0;
                    setForm({ ...form, role_rates: rr });
                  }} className={inpL} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-start gap-2 text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded-xl p-3">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Upah lembur dihitung prorata per menit: (menit lembur ÷ 60) × tarif per jam.
        </div>
        <button data-testid="ot-settings-save" onClick={save} disabled={busy} className="w-full h-10 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Simpan"}</button>
      </div>
    </div>
  );
}

export default function AdminOvertime() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(nowMonth());
  const [role, setRole] = useState("");
  const [empIds, setEmpIds] = useState([]);
  const [tab, setTab] = useState("menunggu");
  const [selKey, setSelKey] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [note, setNote] = useState("");
  const [rateOv, setRateOv] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/overtime", { params: { period, role: role || undefined, employee_id: empIds.join(",") || undefined } })
      .then((r) => setData(r.data)).catch((e) => { setData(null); toast.error(e?.response?.data?.detail || "Gagal memuat data lembur"); });
  }, [period, role, empIds]);
  useEffect(() => { load(); }, [load]);

  const roleLabel = useMemo(() => Object.fromEntries((data?.options?.roles || []).map((r) => [r.id, r.label])), [data]);
  const counts = data?.counts || {};

  const groups = useMemo(() => (data?.groups || [])
    .map((g) => ({ ...g, items: g.items.filter((i) => i.status === tab) }))
    .filter((g) => g.items.length > 0), [data, tab]);

  const sel = groups.find((g) => `${g.date}|${g.role}` === selKey) || null;
  useEffect(() => { setNote(""); setRateOv(""); }, [selKey]);

  const decide = async (ids, action) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await api.put("/overtime/decide", { ids, action, rate: rateOv ? parseInt(rateOv) : undefined, note: note.trim() });
      toast.success(action === "approve" ? `Lembur disetujui (${r.data.processed}) — masuk komponen gaji` : `Lembur ditolak (${r.data.processed}) — absensi tetap sesuai jadwal shift`);
      if (r.data?.payroll_adjustment_needed) toast.warning("Payroll periode terkait sudah dibayar — lakukan penyesuaian manual");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); } finally { setBusy(false); }
  };

  const scan = async () => {
    setBusy(true);
    try {
      const r = await api.post("/overtime/scan", { period });
      toast.success(`Sinkronisasi selesai — ${r.data.found} catatan lembur dari ${r.data.scanned} absensi`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal sinkronisasi"); } finally { setBusy(false); }
  };

  const estOf = (i) => i.status === "disetujui" ? (i.amount || 0) : Math.round((i.minutes || 0) / 60 * (i.rate || 0));
  return (
    <div data-testid="overtime-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Lembur</h1>
          <p className="text-xs text-slate-400 mt-0.5">Lembur tercatat otomatis dari jam pulang melebihi akhir shift. Wajib persetujuan HR sebelum masuk gaji.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="ot-scan-btn" onClick={scan} disabled={busy} className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 text-sm font-semibold flex items-center gap-1.5 hover:border-[#7F1D2D]/30 hover:text-[#7F1D2D] transition-colors disabled:opacity-40"><RefreshCw className="w-4 h-4" /> Sinkronkan</button>
          <button data-testid="ot-settings-btn" onClick={() => setShowSettings(true)} className="w-10 h-10 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-[#7F1D2D] hover:border-[#7F1D2D]/30 transition-colors"><Settings2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div className={`${card} p-4 grid grid-cols-2 lg:grid-cols-3 gap-3`}>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Periode</label>
          <input data-testid="ot-filter-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className={inpL} />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Divisi</label>
          <select data-testid="ot-filter-role" value={role} onChange={(e) => setRole(e.target.value)} className={inpL}>
            <option value="">Semua Divisi</option>
            {(data?.options?.roles || []).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Karyawan</label>
          <EmpPicker tid="ot-filter-emp" options={data?.options?.employees || []} value={empIds} onChange={setEmpIds} placeholder="Ketik nama karyawan" />
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-100 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`ot-tab-${t}`} onClick={() => { setTab(t); setSelKey(null); }}
            className={`px-4 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 ${tab === t ? "border-[#7F1D2D] text-[#7F1D2D]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {TAB_LABEL[t]} <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t ? "bg-[#7F1D2D] text-white" : "bg-slate-100 text-slate-400"}`}>{counts[t] || 0}</span>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-5 items-start">
        <div className="space-y-3">
          {!data && <div className="py-10 text-center text-xs text-slate-400">Memuat...</div>}
          {data && !groups.length && <div data-testid="ot-empty" className={`${card} py-10 text-center text-xs text-slate-400`}>Tidak ada lembur {TAB_LABEL[tab].toLowerCase()} pada filter ini.</div>}
          {groups.map((g) => {
            const key = `${g.date}|${g.role}`;
            const shownMinutes = g.items.reduce((s, i) => s + (i.minutes || 0), 0);
            const avg = (shownMinutes / 60 / g.items.length).toFixed(1);
            const nominal = g.items.reduce((s, i) => s + estOf(i), 0);
            return (
              <div key={key} data-testid={`ot-group-${g.date}-${g.role}`} className={`${card} p-4 flex items-center gap-3 flex-wrap transition-colors ${selKey === key ? "border-[#7F1D2D]/40 bg-[#FBF3F4]/50" : ""}`}>
                <span className="w-10 h-10 rounded-xl bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center flex-shrink-0"><Clock4 className="w-5 h-5" /></span>
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm font-bold text-slate-900">Tim {roleLabel[g.role] || g.role} — {fmtDay(g.date)}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Lembur rata-rata <b className="text-slate-600">{avg} jam</b> • {g.items.length} karyawan • {tab === "disetujui" ? "total" : "estimasi"} <b className="text-[#7F1D2D]">{formatIDR(nominal)}</b></div>
                </div>
                {tab === "menunggu" && (
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`ot-quick-approve-${key}`} onClick={() => decide(g.items.map((i) => i.id), "approve")} disabled={busy} className="h-9 px-3 rounded-xl text-white text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40" style={{ background: MAROON }}><Check className="w-3.5 h-3.5" /> Ya</button>
                    <button data-testid={`ot-quick-reject-${key}`} onClick={() => decide(g.items.map((i) => i.id), "reject")} disabled={busy} className="h-9 px-3 rounded-xl border border-red-200 text-red-500 text-[11px] font-semibold flex items-center gap-1 hover:bg-red-50 disabled:opacity-40"><Ban className="w-3.5 h-3.5" /> Tidak</button>
                  </div>
                )}
                <button data-testid={`ot-detail-btn-${key}`} onClick={() => setSelKey(key)} className="h-9 px-3 rounded-xl border text-[11px] font-semibold transition-colors" style={{ borderColor: MAROON, color: MAROON }}>Detail</button>
              </div>
            );
          })}
          <div className="flex items-center gap-2 text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded-xl p-3">
            <Info className="w-3.5 h-3.5 flex-shrink-0" /> Lembur ditolak tidak mengubah absensi — jam kerja tetap mengikuti jadwal shift.
          </div>
        </div>

        <div className={`${card} p-5 space-y-4 lg:sticky lg:top-4`} data-testid="ot-detail-panel">
          {!sel && <div className="py-16 text-center text-xs text-slate-400">Pilih kelompok lembur untuk melihat rincian per karyawan.</div>}
          {sel && (
            <>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-sm font-bold text-slate-900">Tim {roleLabel[sel.role] || sel.role}</h3>
                  <p className="text-[10px] text-slate-400">{fmtDay(sel.date)} • rata-rata {(sel.items.reduce((s, i) => s + (i.minutes || 0), 0) / 60 / sel.items.length).toFixed(1)} jam</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ST_CHIP[tab]}`}>{TAB_LABEL[tab]}</span>
              </div>
              <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                {sel.items.map((i) => (
                  <div key={i.id} data-testid={`ot-item-${i.id}`} className="bg-slate-50/70 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold text-slate-800">{i.user_name}</div>
                      <div className="text-[11px] font-bold text-[#7F1D2D]">{(i.minutes / 60).toFixed(2)} jam</div>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Akhir shift {i.shift_end} • pulang {i.clock_out} • tarif {formatIDR(i.rate || 0)}/jam</div>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="text-[11px] font-semibold text-emerald-600">{i.status === "disetujui" ? `+${formatIDR(i.amount || 0)}` : `≈ ${formatIDR(estOf(i))}`}</div>
                      {i.status === "menunggu" && (
                        <div className="flex gap-1.5">
                          <button data-testid={`ot-approve-${i.id}`} onClick={() => decide([i.id], "approve")} disabled={busy} className="h-7 px-2.5 rounded-lg text-white text-[10px] font-semibold disabled:opacity-40" style={{ background: MAROON }}>Setujui</button>
                          <button data-testid={`ot-reject-${i.id}`} onClick={() => decide([i.id], "reject")} disabled={busy} className="h-7 px-2.5 rounded-lg border border-red-200 text-red-500 text-[10px] font-semibold hover:bg-red-50 disabled:opacity-40">Tolak</button>
                        </div>
                      )}
                    </div>
                    {i.payroll_adjustment_needed && (
                      <div className="flex items-start gap-1.5 text-[10px] text-amber-700 mt-1.5"><AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> Payroll periode ini sudah dibayar — perlu penyesuaian manual.</div>
                    )}
                    {i.status !== "menunggu" && <div className="text-[9px] text-slate-400 mt-1">Diputuskan oleh {i.decided_by || "—"}{i.note ? ` • ${i.note}` : ""}</div>}
                  </div>
                ))}
              </div>
              {tab === "menunggu" && (
                <div className="space-y-3 border-t border-slate-50 pt-3">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Tarif per jam (opsional — timpa tarif role)</label>
                    <input data-testid="ot-rate-override" type="number" min="0" value={rateOv} onChange={(e) => setRateOv(e.target.value)} placeholder={String(sel.items[0]?.rate || data?.settings?.default_rate || 15000)} className={inpL} />
                  </div>
                  <textarea data-testid="ot-decision-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Catatan keputusan (opsional)..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="ot-reject-all" onClick={() => decide(sel.items.map((i) => i.id), "reject")} disabled={busy} className="h-11 rounded-xl border border-red-200 text-red-500 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-40">Tolak Semua</button>
                    <button data-testid="ot-approve-all" onClick={() => decide(sel.items.map((i) => i.id), "approve")} disabled={busy} className="h-11 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Memproses..." : `Setujui Semua (${sel.items.length})`}</button>
                  </div>
                </div>
              )}
              <div className="border-t border-slate-50 pt-3 space-y-1.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1"><Timer className="w-3 h-3" /> Jejak</div>
                {(sel.items[0]?.history || []).slice(-4).map((h, i) => (
                  <div key={i} className="text-[10px] text-slate-500"><span className="capitalize font-semibold">{h.action}</span> — {h.by}{h.note ? ` • ${h.note}` : ""}</div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {showSettings && <SettingsModal settings={data?.settings} roles={data?.options?.roles || []} onClose={() => setShowSettings(false)} onSaved={load} />}
    </div>
  );
}
